import React from "react";
import { Mail, MapPin, MessageCircle, Phone, Clock } from "lucide-react";
import { Reveal } from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import Seo from "../components/Seo";
import { BRAND, IMAGES } from "../lib/site";

const OFFICES = [
  {
    city: "Bengaluru, India",
    address: "Indiranagar, Bengaluru 560038",
    phone: BRAND.phoneIN,
    hours: "Mon–Sat · 10:00–19:00 IST",
    map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.0793043842726!2d77.6411!3d12.9784!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDU4JzQyLjIiTiA3N8KwMzgnMjcuOSJF!5e0!3m2!1sen!2sin!4v1700000000000",
    testid: "office-bengaluru",
  },
  {
    city: "Dubai, UAE",
    address: "Business Bay, Dubai",
    phone: BRAND.phoneAE,
    hours: "Sun–Thu · 09:00–18:00 GST",
    map: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3611.0!2d55.2708!3d25.1972!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDExJzQ5LjkiTiA1NcKwMTYnMTUuNCJF!5e0!3m2!1sen!2sae!4v1700000000000",
    testid: "office-dubai",
  },
];

const Contact = () => (
  <>
    <Seo
      title="Contact My Hoom — Talk to a Real Estate Advisor"
      path="/contact"
      description="Talk to a My Hoom real estate advisor across Bengaluru and Dubai. WhatsApp, phone, or send us a brief — we respond within one business day."
    />
    <section className="relative bg-navy text-white py-28 lg:py-36 overflow-hidden">
      <div className="absolute inset-0 opacity-30">
        <img src={IMAGES.dubaiAerial} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 to-navy" />
      </div>
      <div className="container-tight relative z-10 max-w-3xl">
        <Reveal>
          <span className="eyebrow text-gold">Contact</span>
          <h1 className="mt-3 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">
            Let's start a <span className="text-gold">conversation</span>.
          </h1>
          <p className="mt-7 text-white/80 text-lg leading-relaxed">
            Share a brief on what you're looking for — a home, a long-term investment, or a commercial asset — and we'll come back with a tailored next step within one business day.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="py-24 lg:py-28">
      <div className="container-tight grid lg:grid-cols-12 gap-14">
        <Reveal className="lg:col-span-7">
          <div className="card-lift p-8 sm:p-10 border border-black/5">
            <span className="eyebrow">Premium enquiry</span>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-navy">Tell us about your goal</h2>
            <div className="mt-7">
              <ContactForm source="contact-page" />
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-5 space-y-5">
          <a
            href={BRAND.whatsappURL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-cta"
            className="block rounded-2xl bg-[#0a3a2d] hover:bg-[#0c4a3a] transition-colors text-white p-7 group"
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow text-[#9aeec1]">Fastest path</div>
                <h3 className="mt-2 font-serif text-2xl">Chat on WhatsApp</h3>
                <p className="mt-2 text-sm text-white/75">Get a personal advisor on chat — often within minutes.</p>
              </div>
              <span className="h-14 w-14 rounded-full bg-[#25D366] grid place-items-center shrink-0 group-hover:scale-105 transition-transform">
                <MessageCircle size={24} strokeWidth={2} />
              </span>
            </div>
          </a>

          <div className="rounded-2xl bg-navy text-white p-7">
            <div className="eyebrow text-gold">Reach us directly</div>
            <ul className="mt-5 space-y-4 text-sm">
              <li className="flex items-center gap-3"><Phone size={16} className="text-gold" />{BRAND.phoneIN} <span className="text-white/40">· Bengaluru</span></li>
              <li className="flex items-center gap-3"><Phone size={16} className="text-gold" />{BRAND.phoneAE} <span className="text-white/40">· Dubai</span></li>
              <li className="flex items-center gap-3"><Mail size={16} className="text-gold" />{BRAND.email}</li>
              <li className="flex items-center gap-3"><Clock size={16} className="text-gold" />Mon–Sat · Same-day response</li>
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    {/* Offices */}
    <section className="bg-[#FAFAFA] py-24 lg:py-28 border-t border-black/5">
      <div className="container-tight">
        <Reveal className="max-w-2xl">
          <span className="eyebrow">Our offices</span>
          <h2 className="mt-3 font-serif text-4xl text-navy">Drop in. We'd love to meet.</h2>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-2 gap-8">
          {OFFICES.map((o, i) => (
            <Reveal key={o.city} delay={i * 0.1}>
              <div data-testid={o.testid} className="card-lift overflow-hidden border border-black/5 bg-white">
                <div className="h-64 w-full bg-navy/5">
                  <iframe
                    title={`Map of ${o.city}`}
                    src={o.map}
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="p-7">
                  <span className="eyebrow text-gold">Office</span>
                  <h3 className="mt-2 font-serif text-2xl text-navy">{o.city}</h3>
                  <ul className="mt-4 space-y-2.5 text-sm text-navy/70">
                    <li className="flex items-start gap-3"><MapPin size={16} className="text-navy/60 mt-0.5" />{o.address}</li>
                    <li className="flex items-center gap-3"><Phone size={16} className="text-navy/60" />{o.phone}</li>
                    <li className="flex items-center gap-3"><Clock size={16} className="text-navy/60" />{o.hours}</li>
                  </ul>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default Contact;
